/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { Modal, PrimaryButton, IconButton } from '@fluentui/react';
import {
    Calendar24Regular,
    Wand24Regular,
    PersonAvailable24Regular,
} from '@fluentui/react-icons';

const wrap = css`
    width: 540px;
    max-width: 94vw;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 24px 64px rgba(15, 32, 64, 0.28);
    position: relative;

    .demo-welcome__close {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 2;
        color: #fff;
        i { color: #fff; }
        &:hover i { color: #fff; }
    }
`;

const banner = css`
    position: relative;
    padding: 22px 28px 22px 28px;

    h2 {
        margin: 0;
        font-size: 28px;
        font-weight: 600;
        line-height: 1.25;
    }
    .demo-welcome__meta {
        font-size: 12px;
        opacity: 0.9;
        margin: 0;
    }
`;

const body = css`
    padding: 0px 28px 22px 28px;
letter-spacing: normal;
    .demo-welcome__intro {
            font-size: 15px;
    line-height: 1.55;
    margin: 0 0 16px 0;
    color: rgba(0, 0, 0, 0.72);
    letter-spacing: normal;
    }
    .demo-welcome__cards {
        display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
    }
    .demo-welcome__card {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }
    .demo-welcome__card-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #1a71b9;
    }
    .demo-welcome__card-icon svg {
        width: 22px;
        height: 22px;
    }
    .demo-welcome__card-title {
        font-size: 15px;
        font-weight: 600;
        margin: 0 0 2px 0;
        color: rgba(0, 0, 0, 0.88);
    }
    .demo-welcome__card-desc {
        line-height: 1.45;
        margin: 0;
        color: rgba(0, 0, 0, 0.62);
    }
    .demo-welcome__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 14px;
    }
    .demo-welcome__explore {
        background: transparent;
        border: none;
        padding: 6px 4px;
        color: #1a71b9;
        cursor: pointer;
    }
        .ms-Button--primary{
            height:40px;
        }
    .demo-welcome__explore:hover { text-decoration: underline; }
`;

const FEATURES = [
    {
        Icon: Calendar24Regular,
        title: 'Build schedules',
        desc: 'Create and publish weekly schedules with conflicts, overtime, and open shifts highlighted.',
    },
    {
        Icon: Wand24Regular,
        title: 'Fill open shifts faster',
        desc: 'Use staffing needs, schedule templates, and AI Auto Schedule to help cover gaps.',
    },
    {
        Icon: PersonAvailable24Regular,
        title: 'Manage employee requests',
        desc: 'Review shift swaps, availability changes, and time-off requests in one place.',
    },
];

/**
 * DemoWelcomeCard - greets a demo user on first dashboard visit and offers
 * either starting the guided tour or exploring on their own.
 */
export default function DemoWelcomeCard({ open, onStart, onDismiss }) {
    if (!open) return null;
    return (
        <Modal
            isOpen={open}
            isBlocking={false}
            onDismiss={onDismiss}
            className="dark-overlay rounded"
            styles={{ layer: { zIndex: '2147483640 !important' } }}
        >
            <div css={wrap} role="document">
                <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    ariaLabel="Close welcome"
                    className="demo-welcome__close"
                    onClick={onDismiss}
                />
                <div css={banner}>
                    <h2>
                        <span role="img" aria-label="waving hand">👋</span> Welcome to Crave Kitchen
                    </h2>
                </div>
                <div css={body}>
                    <p className="demo-welcome__intro">
                        This demo is preloaded with sample employees, schedules, open shifts,
                        requests, and training data so you can explore the full Teamsly
                        experience right away.
                    </p>
                    <div className="demo-welcome__cards">
                        {FEATURES.map((f) => (
                            <div className="demo-welcome__card" key={f.title}>
                                <span className="demo-welcome__card-icon" aria-hidden="true">
                                    <f.Icon />
                                </span>
                                <div>
                                    <p className="demo-welcome__card-title">{f.title}</p>
                                    <p className="demo-welcome__card-desc">{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="demo-welcome__actions">
                        <button
                            type="button"
                            className="demo-welcome__explore"
                            onClick={onDismiss}
                        >
                            Explore on my own
                        </button>
                        <PrimaryButton text="Start guided tour" onClick={onStart} />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
